"use client";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { importSourceInitialState, useImport } from "@/context/importContext";
import {
  decodeCsvBytes,
  detectSeparator,
  parseSourceText,
  sheetRowsToText,
  type ImportSeparatorType,
} from "@/utils/import/importSource";
import { useRef } from "react";
import { FiArrowLeft, FiClipboard, FiFileText, FiTrash2, FiUpload } from "react-icons/fi";
import ImportColumnsReference from "./ImportColumnsReference";
import { useResolvedSource } from "./useResolvedSource";

const MAX_FILE_BYTES = 10 * 1024 * 1024;
const PREVIEW_ROWS = 5;

const SEPARATOR_LABELS: Array<{ value: ImportSeparatorType; label: string }> = [
  { value: ";", label: "Punto y coma" },
  { value: ",", label: "Coma" },
  { value: "\t", label: "Tabulador" },
];

/**
 * Step "upload": where the data comes from. It is pasted (the separator is detected)
 * or read from an Excel or CSV file. The columns are matched to the fields of the
 * profile in the next step, so their order does not matter here.
 */
export default function FileUploader() {
  const { importData, setImportData } = useImport();
  const { source } = importData;
  const { rawRows, hasHeader, resolved } = useResolvedSource();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const updateSource = (change: Partial<typeof source>) =>
    // New data invalidates the columns the user may have matched.
    setImportData((prev) => ({
      ...prev,
      fileErrors: [],
      source: { ...prev.source, mapping: null, ...change },
    }));

  const handlePasteFromClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText();
      updateSource({ text });
    } catch (error) {
      console.error("Failed to read clipboard contents: ", error);
    }
  };

  const handleFile = async (file: File) => {
    const name = file.name.toLowerCase();
    const reset = { fileName: file.name, fileText: null, fileRows: null, fileKind: null, fileError: null };

    if (file.size > MAX_FILE_BYTES) {
      updateSource({ ...reset, fileError: "El archivo es demasiado grande (máximo 10 MB)." });
      return;
    }

    try {
      if (name.endsWith(".xlsx")) {
        // Loaded on demand: only importing an Excel file needs the reader.
        const { readSheet } = await import("read-excel-file/universal");
        const rows = sheetRowsToText(await readSheet(file));
        updateSource({
          ...reset,
          fileKind: "xlsx",
          fileRows: rows,
          fileError: rows.length === 0 ? "La primera hoja del archivo no tiene datos." : null,
        });
      } else if (name.endsWith(".csv") || name.endsWith(".txt") || name.endsWith(".tsv")) {
        const text = decodeCsvBytes(await file.arrayBuffer());
        const separator = detectSeparator(text);
        const rows = parseSourceText(text, separator);
        updateSource({
          ...reset,
          fileKind: "csv",
          fileText: text,
          separator,
          fileRows: rows,
          fileError: rows.length === 0 ? "El archivo no tiene datos." : null,
        });
      } else if (name.endsWith(".xls")) {
        updateSource({
          ...reset,
          fileError: 'El formato .xls es antiguo. Ábrelo en Excel y guárdalo como ".xlsx".',
        });
      } else {
        updateSource({ ...reset, fileError: "Formato no admitido. Usa un archivo .xlsx o .csv." });
      }
    } catch (error) {
      console.error("Error reading the import file:", error);
      updateSource({
        ...reset,
        fileError: "No se pudo leer el archivo. Comprueba que no esté dañado ni protegido con contraseña.",
      });
    }
  };

  const changeSeparator = (separator: ImportSeparatorType) => {
    if (source.fileText === null) return;
    updateSource({
      separator,
      fileRows: parseSourceText(source.fileText, separator),
    });
  };

  const hasData = rawRows.length > 0;
  const previewRows = (resolved.headers ? resolved.dataRows : rawRows).slice(0, PREVIEW_ROWS);
  const previewHeaders =
    resolved.headers ?? Array.from({ length: resolved.columnCount }, (_, index) => `Columna ${index + 1}`);

  return (
    <div className="space-y-4">
      <Card className="border-2">
        <CardContent className="space-y-4 px-6 pb-6">
          <div className="flex gap-2" role="group" aria-label="Origen de los datos">
            <Button
              type="button"
              variant={source.mode === "paste" ? "default" : "outline"}
              size="sm"
              aria-pressed={source.mode === "paste"}
              onClick={() => updateSource({ mode: "paste" })}
            >
              <FiClipboard className="mr-1 h-4 w-4" />
              Pegar datos
            </Button>
            <Button
              type="button"
              variant={source.mode === "file" ? "default" : "outline"}
              size="sm"
              aria-pressed={source.mode === "file"}
              onClick={() => updateSource({ mode: "file" })}
            >
              <FiUpload className="mr-1 h-4 w-4" />
              Subir archivo
            </Button>
          </div>

          {source.mode === "paste" ? (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="import-paste">Pega los datos copiados de Excel o de un CSV</Label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handlePasteFromClipboard}
                    className="flex items-center gap-1"
                  >
                    <FiClipboard className="h-4 w-4" />
                    Pegar
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => updateSource({ text: "", hasHeaderTouched: false })}
                    disabled={!source.text.trim()}
                    className="flex items-center gap-1"
                  >
                    <FiTrash2 className="h-4 w-4" />
                    Borrar
                  </Button>
                </div>
              </div>
              <Textarea
                id="import-paste"
                value={source.text}
                onChange={(event) => updateSource({ text: event.target.value })}
                placeholder="Pega aquí tus datos (Ctrl+V) o usa el botón Pegar"
                rows={8}
                className="font-mono text-xs"
                // The shared text box cuts at 20,000 characters (what a note stores), which
                // is about 170 rows of candidates: pasted data has the limit of a file.
                maxLength={MAX_FILE_BYTES}
              />
            </div>
          ) : (
            <div className="space-y-3">
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.csv,.txt,.tsv"
                className="hidden"
                data-testid="import-file-input"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) void handleFile(file);
                  // The same file can be chosen again after correcting it.
                  event.target.value = "";
                }}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="flex w-full flex-col items-center gap-2 rounded-md border-2 border-dashed border-bg+3 p-6 text-center transition-colors hover:border-ac"
              >
                <FiFileText className="h-8 w-8 text-ac" />
                <span className="text-sm font-medium text-fg">
                  {source.fileName ?? "Elige un archivo de Excel (.xlsx) o CSV"}
                </span>
                <span className="text-xs text-fg-muted">
                  {source.fileName
                    ? "Haz clic para elegir otro archivo"
                    : "Si tiene varias hojas, se lee la primera"}
                </span>
              </button>

              {source.fileError && (
                <p className="rounded-md border border-de bg-de-4 p-3 text-sm text-de">{source.fileError}</p>
              )}

              {source.fileKind === "csv" && (
                <div className="space-y-2">
                  <Label>Separador de columnas del archivo</Label>
                  <RadioGroup
                    value={source.separator}
                    onValueChange={(value) => changeSeparator(value as ImportSeparatorType)}
                    className="flex flex-row gap-x-4"
                  >
                    {SEPARATOR_LABELS.map((option, index) => (
                      <div key={option.value} className="flex items-center space-x-2">
                        <RadioGroupItem value={option.value} id={`import-separator-${index}`} />
                        <Label htmlFor={`import-separator-${index}`}>{option.label}</Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>
              )}
            </div>
          )}

          {hasData && (
            <div className="space-y-3">
              <p className="text-sm text-fg-muted">
                {resolved.dataRows.length} {resolved.dataRows.length === 1 ? "fila" : "filas"} de datos ·{" "}
                {resolved.columnCount} {resolved.columnCount === 1 ? "columna" : "columnas"}
                {resolved.fromTemplate && " · Se reconoció la plantilla de Orquesti"}
              </p>

              {!resolved.fromTemplate && (
                <div className="flex items-center gap-2">
                  <Switch
                    id="import-has-header"
                    checked={hasHeader}
                    onCheckedChange={(checked) =>
                      updateSource({ hasHeader: checked, hasHeaderTouched: true })
                    }
                  />
                  <Label htmlFor="import-has-header">La primera fila es un encabezado</Label>
                </div>
              )}

              <div className="overflow-x-auto rounded-md border border-bg+2">
                <table className="w-full text-xs">
                  <thead className="bg-bg+1 text-left">
                    <tr>
                      {previewHeaders.map((header, index) => (
                        <th key={index} className="whitespace-nowrap p-2 font-medium">
                          {header || `Columna ${index + 1}`}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewRows.map((row, rowIndex) => (
                      <tr key={rowIndex} className="border-t border-bg+2">
                        {Array.from({ length: resolved.columnCount }, (_, index) => (
                          <td key={index} className="max-w-48 truncate whitespace-nowrap p-2">
                            {row[index] ?? ""}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <ImportColumnsReference />

          <div className="flex items-center justify-between gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() =>
                setImportData((prev) => ({ ...prev, fileErrors: [], currentStep: "profile" }))
              }
            >
              <FiArrowLeft className="mr-1 h-4 w-4" />
              Volver a los perfiles
            </Button>
            <div className="flex items-center gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() =>
                  setImportData((prev) => ({
                    ...prev,
                    fileErrors: [],
                    source: { ...importSourceInitialState, mode: prev.source.mode },
                  }))
                }
                disabled={!hasData && !source.fileName}
              >
                Empezar de nuevo
              </Button>
              <Button
                onClick={() => setImportData((prev) => ({ ...prev, currentStep: "columns" }))}
                disabled={resolved.dataRows.length === 0}
              >
                Continuar
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
